/**
 * Meta Pixel helper — Advanced Matching + event dedup with CAPI.
 *
 * Advanced Matching: `fbq('init', PIXEL_ID, userData)` can be called again with
 * additional PII at any point in the session. Pixel SHA-256-hashes everything
 * before transmission, so we pass raw email/phone/address. Match quality on
 * iOS 14.5+ jumps dramatically versus anonymous PageViews.
 *
 * Dedup: Purchase events also fire via CAPI (backend) with the same event_id
 * (= Stripe session_id) so Meta collapses the pair into one conversion.
 */

export const META_PIXEL_ID = "4400865146801178";

/** Normalise a customer object into the Pixel Advanced Matching schema. */
const toMatchingPayload = (customer) => {
  if (!customer || typeof customer !== "object") return null;
  const out = {};
  if (customer.email) out.em = String(customer.email).trim().toLowerCase();
  if (customer.phone) out.ph = String(customer.phone).replace(/\D/g, "");
  if (customer.first_name) out.fn = String(customer.first_name).trim().toLowerCase();
  if (customer.last_name) out.ln = String(customer.last_name).trim().toLowerCase();
  if (customer.city) out.ct = String(customer.city).replace(/\s+/g, "").toLowerCase();
  if (customer.state) out.st = String(customer.state).replace(/\s+/g, "").toLowerCase();
  if (customer.zip) out.zp = String(customer.zip).replace(/\s+/g, "").toLowerCase();
  if (customer.country) out.country = String(customer.country).trim().toLowerCase();
  return Object.keys(out).length ? out : null;
};

/**
 * Re-init the Pixel with hashed Advanced Matching data. No-op if fbq isn't
 * loaded (ad-blocker) or we've already matched this exact payload this session.
 */
export const setAdvancedMatching = (customer) => {
  try {
    if (typeof window === "undefined" || typeof window.fbq !== "function") return;
    const payload = toMatchingPayload(customer);
    if (!payload) return;
    const sig = JSON.stringify(payload);
    if (window.__tracksyPixelMatchSig === sig) return;
    window.__tracksyPixelMatchSig = sig;
    window.fbq("init", META_PIXEL_ID, payload);
  } catch {
    /* non-fatal — attribution plumbing */
  }
};

/**
 * Read a cookie by name. Returns "" when missing or SSR.
 */
const readCookie = (name) => {
  try {
    if (typeof document === "undefined") return "";
    const m = document.cookie.match(new RegExp("(?:^|; )" + name + "=([^;]*)"));
    return m ? decodeURIComponent(m[1]) : "";
  } catch {
    return "";
  }
};

/**
 * Pull the Meta attribution signals (_fbp cookie, _fbc click id derived from
 * ?fbclid=, and user agent) that CAPI needs to match an off-site conversion
 * back to the original ad click. Safe to call anywhere — all fields default
 * to "" when the Pixel never loaded.
 *
 * Spec: _fbc = `fb.1.<ts>.<fbclid>`. If the pixel wrote the cookie we trust
 * it; otherwise we build it from `?fbclid` on the current URL so a direct
 * ad-click → cart-add → checkout flow still carries the click id.
 */
export const getAttribution = () => {
  try {
    const fbp = readCookie("_fbp") || "";
    let fbc = readCookie("_fbc") || "";
    if (!fbc && typeof window !== "undefined") {
      const fbclid = new URLSearchParams(window.location.search).get("fbclid");
      if (fbclid) fbc = `fb.1.${Date.now()}.${fbclid}`;
    }
    const user_agent = typeof navigator !== "undefined" ? navigator.userAgent || "" : "";
    return { fbp, fbc, user_agent };
  } catch {
    return { fbp: "", fbc: "", user_agent: "" };
  }
};
